import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, Clock, FileText, AlertCircle } from 'lucide-react';
import TaxFormModal from './TaxFormModal';
import './Assignments.css';

const Assignments = ({ currentUser }) => {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    // Load assignments from localStorage
    const savedAssignments = localStorage.getItem('employee_assignments');
    if (savedAssignments) {
      setAssignments(JSON.parse(savedAssignments));
    } else {
      // Initialize with default onboarding and sample assignments
      const defaultAssignments = [
        {
          id: 'onboarding-w2',
          title: 'Complete W-2 Form',
          description: 'Fill out and submit your W-2 tax withholding form',
          category: 'Onboarding',
          dueDate: '2026-01-15',
          completed: false,
          priority: 'High',
          assignedBy: 'Jennifer Major (HR)',
          formType: 'w2'
        },
        {
          id: 'onboarding-w4',
          title: 'Complete W-4 Form',
          description: "Fill out and submit your W-4 Employee's Withholding Certificate",
          category: 'Onboarding',
          dueDate: '2026-01-15',
          completed: false,
          priority: 'High',
          assignedBy: 'Jennifer Major (HR)',
          formType: 'w4'
        },
        {
          id: 'onboarding-handbook',
          title: 'Review Employee Handbook',
          description: 'Read and acknowledge the AG&WESTERN Rail Employee Handbook',
          category: 'Onboarding',
          dueDate: '2026-01-10',
          completed: false,
          priority: 'High',
          assignedBy: 'Jennifer Major (HR)'
        },
        {
          id: 'onboarding-safety',
          title: 'Safety Training Certification',
          description: 'Complete mandatory rail safety training and certification',
          category: 'Onboarding',
          dueDate: '2026-01-20',
          completed: false,
          priority: 'High',
          assignedBy: 'Safety Department'
        },
        {
          id: 'task-1',
          title: 'Q4 Performance Review',
          description: 'Complete self-assessment for Q4 performance review',
          category: 'Administrative',
          dueDate: '2026-12-30',
          completed: false,
          priority: 'Medium',
          assignedBy: 'Madison Bailey'
        }
      ];
      setAssignments(defaultAssignments);
      localStorage.setItem('employee_assignments', JSON.stringify(defaultAssignments));
    }
  }, []);

  const toggleComplete = (assignmentId) => {
    const updatedAssignments = assignments.map(assignment => {
      if (assignment.id === assignmentId) {
        return {
          ...assignment,
          completed: !assignment.completed,
          completedDate: !assignment.completed ? new Date().toISOString() : null
        };
      }
      return assignment;
    });
    
    setAssignments(updatedAssignments);
    localStorage.setItem('employee_assignments', JSON.stringify(updatedAssignments));
  };

  const handleFormSubmit = (assignmentId) => {
    // Mark assignment as completed after form submission
    toggleComplete(assignmentId);
    setSelectedAssignment(null);
  };

  const handleAssignmentClick = (assignment) => {
    // If it's a form assignment (w2 or w4), open the modal
    if (assignment.formType && !assignment.completed) {
      setSelectedAssignment(assignment);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (assignment) => {
    if (assignment.completed) return 'completed';
    const daysUntil = getDaysUntilDue(assignment.dueDate);
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 3) return 'urgent';
    if (daysUntil <= 7) return 'soon';
    return 'normal';
  };

  const getStatusText = (assignment) => {
    if (assignment.completed) {
      return `Completed on ${new Date(assignment.completedDate).toLocaleDateString()}`;
    }
    const daysUntil = getDaysUntilDue(assignment.dueDate);
    if (daysUntil < 0) return `Overdue by ${Math.abs(daysUntil)} days`;
    if (daysUntil === 0) return 'Due today';
    if (daysUntil === 1) return 'Due tomorrow';
    return `Due in ${daysUntil} days`;
  };

  const sortedAssignments = [...assignments].sort((a, b) => {
    // Completed items go to bottom
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    // Then sort by due date
    return new Date(a.dueDate) - new Date(b.dueDate);
  });

  const pendingCount = assignments.filter(a => !a.completed).length;
  const overdueCount = assignments.filter(a => !a.completed && getDaysUntilDue(a.dueDate) < 0).length;

  return (
    <div className="assignments-container">
      {/* Summary Stats */}
      <div className="assignments-stats">
        <div className="stat-card">
          <div className="stat-icon pending">
            <Clock size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-number">{pendingCount}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon overdue">
            <AlertCircle size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-number">{overdueCount}</div>
            <div className="stat-label">Overdue</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon completed">
            <CheckCircle size={24} />
          </div>
          <div className="stat-info">
            <div className="stat-number">{assignments.filter(a => a.completed).length}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="assignments-list">
        <h3>Your Assignments</h3>
        {sortedAssignments.map(assignment => (
          <div 
            key={assignment.id} 
            className={`assignment-card ${getStatusColor(assignment)} ${assignment.formType ? 'clickable' : ''}`}
            onClick={() => handleAssignmentClick(assignment)}
            style={{ cursor: assignment.formType && !assignment.completed ? 'pointer' : 'default' }}
          >
            <div className="assignment-checkbox">
              <input
                type="checkbox"
                checked={assignment.completed}
                onChange={() => toggleComplete(assignment.id)}
                id={`assignment-${assignment.id}`}
              />
              <label htmlFor={`assignment-${assignment.id}`}></label>
            </div>

            <div className="assignment-content">
              <div className="assignment-header">
                <h4 className={assignment.completed ? 'completed-text' : ''}>
                  {assignment.title}
                </h4>
                <div className="assignment-badges">
                  <span className={`category-badge ${assignment.category.toLowerCase()}`}>
                    {assignment.category}
                  </span>
                  {assignment.priority && (
                    <span className={`priority-badge ${assignment.priority.toLowerCase()}`}>
                      {assignment.priority}
                    </span>
                  )}
                </div>
              </div>

              <p className="assignment-description">{assignment.description}</p>

              <div className="assignment-meta">
                <div className="meta-item">
                  <Calendar size={16} />
                  <span>Due: {new Date(assignment.dueDate).toLocaleDateString()}</span>
                </div>
                <div className="meta-item">
                  <FileText size={16} />
                  <span>Assigned by: {assignment.assignedBy}</span>
                </div>
              </div>

              <div className={`assignment-status status-${getStatusColor(assignment)}`}>
                {getStatusText(assignment)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tax Form Modal */}
      {selectedAssignment && (
        <TaxFormModal
          assignment={selectedAssignment}
          currentUser={currentUser}
          onClose={() => setSelectedAssignment(null)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default Assignments;
